import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Calculator, GlobeHemisphereWest, ChatCircleText, Rocket, ShieldCheck, Buildings, Phone, TrendUp, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useCountry } from "@/lib/country";
import { useSettings } from "@/lib/settings";
import MapleLeaf from "@/components/MapleLeaf";
import JourneyAnimation, { JourneyAnimationMobile } from "@/components/JourneyAnimation";
import GlobalNetwork from "@/components/GlobalNetwork";
import RotatingHeroLine from "@/components/RotatingHeroLine";
import StickyCalcCTA from "@/components/StickyCalcCTA";
import {
  HomeGoals,
  HomeBenefits,
  HomeWhoFor,
  HomeSimpleProcess,
  HomeCanadaCTA,
} from "@/components/HomeConversion";
import { HandPointing } from "@phosphor-icons/react";

const TRUST = [
  "Canadian-Based Company",
  "Non-Resident Support",
  "Company Formation",
  "Virtual Office Solutions",
  "Banking Assistance",
  "Global Business Expansion",
  "Business Development Support",
  "Professional Referral Network",
];

export default function Home() {
  const [countries, setCountries] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const { setCountry } = useCountry();
  const navigate = useNavigate();
  const settings = useSettings();

  const activeCountries = countries.filter((c) => c.active !== false);
  const countryCount = activeCountries.length;
  const currencyCount = new Set(activeCountries.map((c) => c.currency_code)).size;
  const countryListShort = activeCountries.slice(0, 6).map((c) => c.name).join(" · ")
    + (countryCount > 6 ? ` · +${countryCount - 6} more` : "");
  const countryListLong = activeCountries.map((c) => c.name).join(", ");

  // Homepage hero — admin-editable (falls back to defaults if key missing)
  const hero = settings.homepage_hero || {};
  const trustItems = (hero.trust_indicators && hero.trust_indicators.length) ? hero.trust_indicators : TRUST;
  const heroCtas = [
    { label: hero.cta_primary_label || "Explore Your Options", url: hero.cta_primary_url || "/countries", variant: "primary", icon: GlobeHemisphereWest, testid: "hero-explore-btn" },
    { label: hero.cta_secondary_label || "Calculate Setup Cost", url: hero.cta_secondary_url || "/calculator", variant: "secondary", icon: Calculator, testid: "hero-calculate-btn" },
    { label: hero.cta_tertiary_label || "Book a Free Consultation", url: hero.cta_tertiary_url || "/contact", variant: "sand", icon: ChatCircleText, testid: "hero-consult-btn" },
  ];

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/case-studies").then((r) => setCaseStudies(r.data.slice(0, 3)));
  }, []);

  const pickCountry = (slug) => {
    setCountry(slug);
    navigate(`/countries/${slug}`);
  };

  return (
    <div data-testid="home-page" className="fade-in">
      {/* Hero — premium light editorial with animated journey story */}
      <section className="relative overflow-hidden border-b border-gos-border" style={{ background: "var(--gos-bone)" }}>
        <div className="grain absolute inset-0" />
        <div className="container-gos grid grid-cols-1 lg:grid-cols-12 gap-12 items-center py-20 lg:py-28 relative">
          <div className="lg:col-span-7">
            <div className="overline mb-6 flex items-center gap-2">
              <MapleLeaf size={14} className="text-gos-maple" />
              <span>{settings.brand_name} · Canadian-based business advisory</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.04] text-gos-charcoal" data-testid="hero-headline">
              {hero.headline_line1 || "Open Your Business"}<br />
              <span className="italic text-gos-navy">{hero.headline_line2 || "Beyond Borders"}</span>
              <span className="text-gos-sand">.</span>
            </h1>
            <p className="mt-8 text-base lg:text-lg text-gos-slate max-w-xl leading-relaxed" data-testid="hero-subheadline">
              {hero.subheadline || "A Canadian-based company helping entrepreneurs establish businesses, build a professional presence, and expand into international markets."}
            </p>
            <div className="mt-10 flex flex-wrap items-start gap-3 relative" data-testid="hero-ctas">
              {heroCtas.map((cta) => {
                const Icon = cta.icon;
                const BTN_CLS = { primary: "btn-primary", sand: "btn-sand", secondary: "btn-secondary" };
                const cls = BTN_CLS[cta.variant] ?? BTN_CLS.secondary;
                const isCalc = cta.testid === "hero-calculate-btn";
                return (
                  <div key={cta.testid} className={isCalc ? "relative" : ""}>
                    {isCalc && (
                      <>
                        {/* Subtle animated glow ring behind Calculate Setup Cost */}
                        <span aria-hidden="true" className="absolute inset-0 rounded-sm animate-pulse-glow pointer-events-none" />
                        {/* Pointing hand hovering just above-right of the button */}
                        <HandPointing
                          size={26} weight="fill"
                          className="hidden md:block absolute -top-6 -right-6 text-gos-maple animate-point-nudge pointer-events-none rotate-[35deg]"
                          aria-hidden="true"
                        />
                      </>
                    )}
                    <Link
                      to={cta.url}
                      data-testid={cta.testid}
                      className={`${cls} ${isCalc ? "relative shadow-[0_0_0_3px_rgba(212,167,79,0.35)]" : ""}`}
                    >
                      <Icon size={16} weight="bold" /> {cta.label}
                    </Link>
                  </div>
                );
              })}
            </div>
            {/* Supporting nudge under CTAs */}
            <p className="mt-3 text-xs text-gos-muted" data-testid="hero-calc-nudge">
              <span className="text-gos-maple font-semibold">Not sure where to start?</span> See your estimated setup cost in under 60 seconds.
            </p>

            {/* Rotating hero line */}
            <RotatingHeroLine />
            {/* Hero trust chips */}
            <div className="mt-8 flex flex-wrap gap-x-4 gap-y-2 max-w-xl" data-testid="hero-trust-chips">
              {trustItems.map((t) => (
                <div key={t} className="flex items-center gap-1.5 text-xs text-gos-slate">
                  <CheckCircle size={13} weight="duotone" className="text-gos-success shrink-0" /> {t}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Desktop / tablet: full 5-scene animation */}
              <div className="hidden md:block">
                <JourneyAnimation
                  enabled={hero.animation_enabled !== false}
                  speed={hero.animation_speed || 1.0}
                  captions={hero.scene_captions || []}
                  expansionCountries={hero.expansion_countries || []}
                  fallbackImageUrl={hero.fallback_image_url || ""}
                  brandName={settings.brand_name}
                />
              </div>
              {/* Mobile: simplified 4-step vertical version if enabled */}
              <div className="block md:hidden">
                {hero.mobile_simplified !== false ? (
                  <JourneyAnimationMobile
                    enabled={hero.animation_enabled !== false}
                    captions={hero.scene_captions || []}
                    brandName={settings.brand_name}
                  />
                ) : (
                  <JourneyAnimation
                    enabled={hero.animation_enabled !== false}
                    speed={hero.animation_speed || 1.0}
                    captions={hero.scene_captions || []}
                    expansionCountries={hero.expansion_countries || []}
                    fallbackImageUrl={hero.fallback_image_url || ""}
                    brandName={settings.brand_name}
                  />
                )}
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white border border-gos-border p-5 max-w-[230px] hidden md:block" data-testid="hero-active-markets">
                <div className="overline text-gos-sand flex items-center gap-1.5"><MapleLeaf size={11} className="text-gos-maple" /> Active markets</div>
                <div className="font-display text-3xl text-gos-navy mt-1">{countryCount} {countryCount === 1 ? "Country" : "Countries"}</div>
                <div className="text-xs text-gos-muted mt-1">{countryListShort}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate trust band — by-the-numbers credibility */}
      <section className="border-b border-gos-border bg-white" data-testid="trust-metrics">
        <div className="container-gos grid grid-cols-2 md:grid-cols-4 divide-x divide-gos-border">
          <TrustMetric value={String(countryCount)} label="Active markets" testid="metric-active-markets" />
          <TrustMetric value="20+" label="Advisory services" />
          <TrustMetric value={String(currencyCount)} label="Currencies supported" testid="metric-currencies" />
          <TrustMetric value={`Since ${settings.established_year || 2020}`} label="Operating year" />
        </div>
      </section>

      {/* Trust badges strip */}
      <section className="border-b border-gos-border" style={{ background: "var(--gos-subtle)" }}>
        <div className="container-gos grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-y-4 py-5 text-xs">
          {TRUST.map((t) => (
            <div key={t} className="flex items-start gap-2 text-gos-slate"><CheckCircle size={14} weight="duotone" className="text-gos-success mt-0.5 shrink-0" /> {t}</div>
          ))}
        </div>
      </section>

      {/* NEW: What Do You Want To Do? */}
      <HomeGoals />

      {/* NEW: One Setup. Bigger Possibilities. */}
      <HomeBenefits />

      {/* Country picker */}
      <section className="py-24 bg-gos-bone" data-testid="country-pick-section">
        <div className="container-gos">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <div className="overline mb-3">Step 1</div>
              <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">Where would you like to establish your business?</h2>
            </div>
            <Link to="/countries" className="text-sm text-gos-navy hover:text-gos-sand inline-flex items-center gap-1" data-testid="all-countries-link">All countries <ArrowRight size={14} /></Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {countries.map((c) => (
              <button
                key={c.slug}
                data-testid={`home-country-${c.slug}`}
                onClick={() => pickCountry(c.slug)}
                className="card-lift group bg-white border border-gos-border p-6 text-left rounded-sm"
              >
                <div className="text-4xl mb-3">{c.flag}</div>
                <div className="font-display text-lg text-gos-navy">{c.name}</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-gos-sand mt-1">{c.currency_code}</div>
                <div className="text-xs text-gos-muted mt-3 leading-relaxed line-clamp-2">{c.tagline || c.short_description}</div>
                <div className="mt-4 text-xs text-gos-navy inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight size={12} weight="bold" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Service categories */}
      <section className="py-24" style={{ background: "var(--gos-subtle)" }}>
        <div className="container-gos">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <div className="overline mb-3">Services</div>
              <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">Everything you need to launch and scale — in one place.</h2>
            </div>
            <p className="lg:col-span-7 text-gos-slate self-end leading-relaxed">
              From the first day of incorporation to your tenth international office, we coordinate setup, presence, growth, expansion and professional referrals through our trusted partner network.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: Buildings, label: "Business Setup", desc: "Incorporation, federal/provincial registration, tax IDs, annual compliance." },
              { icon: Phone, label: "Business Presence", desc: "Virtual offices, mail handling, business phone, meeting rooms." },
              { icon: TrendUp, label: "Business Growth", desc: "BD managers, lead generation, distributor & partner search, market research." },
              { icon: Rocket, label: "Investment & Expansion", desc: "Market entry, investor intros, franchise & acquisition support." },
              { icon: ShieldCheck, label: "Professional Referrals", desc: "Accounting, legal, tax, banking and immigration partners." },
            ].map((s) => (
              <div key={s.label} className="card-lift bg-white border border-gos-border p-6 rounded-sm">
                <s.icon size={28} weight="duotone" className="text-gos-navy mb-4" />
                <div className="font-display text-lg text-gos-navy mb-2">{s.label}</div>
                <p className="text-sm text-gos-slate leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="explore-services-btn" className="btn-secondary">Explore all services <ArrowRight size={14} weight="bold" /></Link>
          </div>
        </div>
      </section>

      {/* Simple 3-step process (replaces the previous 10-step version to keep the page clean) */}
      <HomeSimpleProcess />

      {/* NEW: Who Is OBA For? */}
      <HomeWhoFor />

      {/* NEW: Compact Canada CTA */}
      <HomeCanadaCTA />

      {/* Case studies */}
      <section className="py-24 bg-white border-y border-gos-border">
        <div className="container-gos">
          <div className="flex justify-between items-end mb-10">
            <div>
              <div className="overline mb-3">Case studies</div>
              <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">Real founders, crossing borders.</h2>
            </div>
            <Link to="/case-studies" className="text-sm text-gos-navy hover:text-gos-sand inline-flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.map((cs) => (
              <Link to={`/case-studies/${cs.slug}`} key={cs.id} className="card-lift bg-white border border-gos-border rounded-sm overflow-hidden" data-testid={`home-case-${cs.slug}`}>
                {cs.image_url && <img src={cs.image_url} alt={cs.title} className="w-full aspect-[16/10] object-cover" />}
                <div className="p-6">
                  <div className="overline text-gos-sand mb-2">{cs.client_origin} → {cs.target_country}</div>
                  <div className="font-display text-xl text-gos-navy leading-snug">{cs.title}</div>
                  <p className="text-sm text-gos-slate mt-3 line-clamp-3">{cs.challenge}</p>
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-gos-muted mt-6">Illustrative examples based on common client scenarios.</p>
        </div>
      </section>

      {/* Our Global Network + Our Global Partners (content-only insert) */}
      <GlobalNetwork />

      {/* CTA */}
      <section className="py-24" style={{ background: "var(--gos-navy)" }}>
        <div className="container-gos grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 text-gos-bone">
            <div className="overline text-gos-sand mb-3">Ready when you are</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight leading-tight">Talk to our consultants. Plan the move.</h2>
            <p className="mt-4 text-gos-bone/80 max-w-2xl leading-relaxed">
              In a 30-minute call we'll map the right structure, pricing, and timeline for your business — across one or several markets.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/contact" className="btn-sand">Book Consultation</Link>
            <Link to="/calculator" className="btn-secondary !bg-transparent !text-white !border-white/40 hover:!bg-white hover:!text-gos-navy">Get an instant quote</Link>
          </div>
        </div>
      </section>

      {/* Mobile-only sticky Calculate CTA (hidden on ≥md) */}
      <StickyCalcCTA />
    </div>
  );
}

function Metric({ value, sub, label }) {
  return (
    <div className="p-5">
      <div className="font-display text-3xl text-white tracking-tight">
        {value} {sub && <span className="text-gos-accent">{sub}</span>}
      </div>
      <div className="overline mt-2 !text-white/50">{label}</div>
    </div>
  );
}

function TrustMetric({ value, label, testid }) {
  return (
    <div className="p-6 md:p-8" data-testid={testid}>
      <div className="font-display text-4xl text-gos-navy tracking-tight">{value}</div>
      <div className="overline mt-2">{label}</div>
    </div>
  );
}
